import { useState, useEffect } from 'react';
import { Menu, Sun, Moon, Globe, X } from 'lucide-react';
import AdminSidebar from './Sidebar';
import { useSettings } from '../../context/SettingsContext';
import { useAuth } from '../../context/AuthContext';

const LANGS = [{ code: 'en', label: 'EN', flag: '🇬🇧' }, { code: 'fr', label: 'FR', flag: '🇫🇷' }, { code: 'ar', label: 'AR', flag: '🇸🇦' }];

export default function AdminLayout({ children, title }) {
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [langOpen, setLangOpen] = useState(false);
  const { theme, toggleTheme, lang, setLang, t } = useSettings();
  const { user } = useAuth();

  // Bloquer scroll quand sidebar mobile ouverte
  useEffect(() => {
    document.body.style.overflow = mobileOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [mobileOpen]);

  return (
    <div className="admin-layout" style={{ background: 'var(--bg-primary)', minHeight: '100vh' }}>
      <AdminSidebar
        collapsed={collapsed}
        setCollapsed={setCollapsed}
        mobileOpen={mobileOpen}
        setMobileOpen={setMobileOpen}
      />

      <div
        className="admin-main"
        style={{
          marginInlineStart: collapsed ? 72 : 270,
          transition: 'margin 0.22s',
        }}
      >
        {/* ── TOPBAR ── */}
        <div className="admin-topbar">
          {/* Bouton hamburger — visible seulement sur mobile */}
          <button
            className="btn btn-ghost btn-icon admin-menu-mobile"
            onClick={() => setMobileOpen(true)}
            style={{ display: 'none' }}
          >
            <Menu size={20} />
          </button>

          {/* Bouton collapse sidebar — visible seulement sur desktop */}
          <button
            className="btn btn-ghost btn-icon admin-menu-desktop"
            onClick={() => setCollapsed(!collapsed)}
          >
            <Menu size={20} />
          </button>

          <h1
            className="admin-topbar-title"
            style={{ fontFamily: 'var(--font-display)', fontSize: 20, fontWeight: 700, flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
          >
            {title || t('admin', 'overview')}
          </h1>

          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginInlineStart: 'auto', flexShrink: 0 }}>
            {/* Lang switcher */}
            <div style={{ position: 'relative' }}>
              <button
                className="btn btn-secondary btn-sm"
                onClick={() => setLangOpen(!langOpen)}
                style={{ gap: 6, padding: '7px 10px' }}
              >
                <Globe size={15} />
                <span className="hide-mobile-inline">{LANGS.find(l => l.code === lang)?.flag} {LANGS.find(l => l.code === lang)?.label}</span>
                <span className="show-mobile-inline" style={{ display: 'none' }}>{LANGS.find(l => l.code === lang)?.flag}</span>
              </button>
              {langOpen && (
                <div style={{ position: 'absolute', top: '100%', insetInlineEnd: 0, marginTop: 6, background: 'var(--bg-card)', border: '1.5px solid var(--border)', borderRadius: 14, padding: 6, minWidth: 120, zIndex: 50, boxShadow: 'var(--shadow)' }}>
                  {LANGS.map(l => (
                    <button key={l.code} onClick={() => { setLang(l.code); setLangOpen(false); }}
                      style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '9px 14px', width: '100%', background: lang === l.code ? 'var(--accent-dim)' : 'none', border: 'none', cursor: 'pointer', borderRadius: 10, color: lang === l.code ? 'var(--accent)' : 'var(--text-primary)', fontSize: 14, fontWeight: lang === l.code ? 700 : 400 }}>
                      {l.flag} {l.label}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Theme toggle */}
            <button className="btn btn-secondary btn-icon" onClick={toggleTheme}>
              {theme === 'dark' ? <Sun size={17} /> : <Moon size={17} />}
            </button>

            {/* User avatar */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 12px 6px 6px', background: 'var(--bg-hover)', borderRadius: 12, border: '1px solid var(--border)' }}>
              <div style={{ width: 30, height: 30, borderRadius: '50%', background: 'var(--accent)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, fontWeight: 800, color: 'white', flexShrink: 0 }}>
                {user?.name?.[0]?.toUpperCase()}
              </div>
              <span className="admin-user-name" style={{ fontSize: 14, fontWeight: 600 }}>{user?.name?.split(' ')[0]}</span>
            </div>
          </div>
        </div>

        {/* ── CONTENU ── */}
        <div className="admin-content">
          {children}
        </div>
      </div>
    </div>
  );
}